import { ref, readonly } from 'vue'
import type { NFT } from '../types/nft'

// Types
export interface GetNFTsOptions {
  pageKey?: string
  excludeSpam?: boolean
  excludeAirdrops?: boolean
  sortBy?: string
  sortDirection?: string
}

export interface GetNFTsResponse {
  ownedNfts: NFT[]
  pageKey?: string
  totalCount: number
}

export interface FloorPriceResponse {
  floorPrice: number
  timestamp: string
  source: string
}

// Re-export the NFT type for convenience
export type { NFT } from '../types/nft'

export interface CollectionValueData {
  floorPrice: FloorPriceResponse | null
  sales: object | null
}

/**
 * NFT API Composable
 * Handles all API calls to the backend for fetching NFTs and collection data
 */
export function useNftApi(debugMode: boolean = false) {
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const abortController = ref<AbortController | null>(null)

  // Debug function
  const debug = (...args: any[]) => {
    if (debugMode) {
      console.log('[NFT API]', ...args)
    }
  }

  /**
   * Abort all in-flight requests
   */
  const cancelRequests = () => {
    if (abortController.value) {
      abortController.value.abort()
    }
    abortController.value = new AbortController()
    // Clear any error state when cancelling requests
    error.value = null
  }

  /**
   * Load NFTs for a wallet address
   */
  const loadNFTs = async (
    walletAddress: string, 
    options: GetNFTsOptions = {}
  ): Promise<GetNFTsResponse> => {
    if (!abortController.value) {
      abortController.value = new AbortController()
    }

    isLoading.value = true
    error.value = null

    try {
      debug('Loading NFTs for address:', walletAddress)

      // Build URL with query parameters
      const url = new URL(`/api/nfts/${walletAddress}`, window.location.origin)

      // Add query parameters
      if (options.pageKey) {
        url.searchParams.set('pageKey', options.pageKey)
      }
      if (options.excludeSpam !== undefined && !options.excludeSpam) {
        url.searchParams.append('excludeFilters[]', 'SPAM')
      }
      if (options.excludeAirdrops !== undefined && !options.excludeAirdrops) {
        url.searchParams.append('excludeFilters[]', 'AIRDROPS')
      }

      // Add sorting parameters
      if (options.sortBy) {
        url.searchParams.set('orderBy', options.sortBy)
      }
      if (options.sortDirection) {
        url.searchParams.set('orderDirection', options.sortDirection)
      }

      debug('Making request to:', url.toString())

      const response = await fetch(url, { 
        signal: abortController.value.signal 
      })
      debug('Response status:', response.status)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Error response:', errorText)
        throw new Error(`Failed to fetch NFTs: ${response.status} ${response.statusText}`)
      }

      const data: GetNFTsResponse = await response.json()
      debug('Page data received:', data)
      debug('Owned NFTs count:', data.ownedNfts?.length || 0)
      debug('Page key:', data.pageKey)
      debug('Total count:', data.totalCount)

      return data

    } catch (err) {
      const errorObj = err as Error
      if (errorObj.name === 'AbortError') {
        debug('NFT fetch aborted')
        throw errorObj
      }
      console.error('Error loading NFTs:', errorObj)
      error.value = errorObj.message
      throw errorObj
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Load collection values (floor prices and sales data)
   */
  const loadCollectionValues = async (
    contracts: string[]
  ): Promise<Map<string, CollectionValueData>> => {
    if (!abortController.value) {
      abortController.value = new AbortController()
    }

    const results = new Map<string, CollectionValueData>()

    for (const contract of contracts) {
      try {
        // Get floor price
        const floorPriceUrl = new URL(`/api/nft/floor-price/${contract}`, window.location.origin)
        debug(`Fetching floor price from: ${floorPriceUrl}`)
        const floorResponse = await fetch(floorPriceUrl, { 
          signal: abortController.value.signal 
        })
        const floorData: FloorPriceResponse | null = floorResponse.ok ? await floorResponse.json() : null
        debug(`Floor price response for ${contract}:`, floorData)

        // Get sales data
        const salesUrl = new URL(`/api/nft/sales/${contract}`, window.location.origin)
        debug(`Fetching sales from: ${salesUrl}`)
        const salesResponse = await fetch(salesUrl, { 
          signal: abortController.value.signal 
        })
        const salesData: object | null = salesResponse.ok ? await salesResponse.json() : null
        debug(`Sales response for ${contract}:`, salesData)

        results.set(contract, {
          floorPrice: floorData,
          sales: salesData
        })

        debug(`Loaded data for contract ${contract}`, {
          floorPrice: floorData,
          sales: salesData
        })

      } catch (err) {
        const errorObj = err as Error
        if (errorObj.name === 'AbortError') {
          debug(`Fetch aborted for contract ${contract}`)
          throw errorObj
        }
        debug(`Error loading data for contract ${contract}:`, errorObj)
        results.set(contract, { floorPrice: null, sales: null })
      }
    }

    return results
  }

  return {
    isLoading: readonly(isLoading),
    error: readonly(error),
    loadNFTs,
    loadCollectionValues,
    cancelRequests
  }
} 